import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSaleDetail, getFirmDetails, getSalePDF, openPdfInNewWindow } from '../services/api';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Button,
  Grid,
  Divider
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

const InvoiceTemplate = () => {
  const { id } = useParams();                  // <-- get id param
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const [firm, setFirm] = useState({});

  useEffect(() => {
    if (!id) {
      console.error('InvoiceTemplate: no id in URL');
      navigate('/error');
      return;
    }
    const fetchData = async () => {
      try {
        const [saleRes, firmRes] = await Promise.all([
          getSaleDetail(id),
          getFirmDetails()
        ]);
        if (!saleRes.items) {
          throw new Error('Invalid sale data');
        }
        setSale(saleRes);
        setFirm(firmRes);
      } catch (error) {
        console.error('Loading failed:', error);
        navigate('/error');
      }
    };
    fetchData();
  }, [id, navigate]);

  const handlePrint = () => {
    window.print();
  };

  const handleViewPDF = async () => {
    if (!sale) return;
    const pdfWindow = window.open('', '_blank');
    if (!pdfWindow) {
      alert('Please allow pop‑ups for this site to view the invoice.');
      return;
    }
    try {
      const pdfBlob = await getSalePDF(sale.id);
      const blobUrl = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      pdfWindow.location.href = blobUrl;
    } catch (error) {
      console.error('PDF Error:', error);
      pdfWindow.close();
      alert('Failed to load PDF');
    }
  };
  

  if (!sale) return <div>Loading...</div>;

  // calculate payments summary
  const amountPaid = sale.payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
  const balanceDue = sale.total_amount - amountPaid;


  return (
    <Box sx={{ p: 4, bgcolor: 'background.paper' }} className="invoice-container">
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />} 
            onClick={handlePrint}
          >
            Print
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={handleViewPDF}>
            View PDF
          </Button>
        </Grid>
      </Grid>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>{firm.name}</Typography>
        <Typography variant="body1">GSTIN: {firm.gstin}</Typography>
        <Typography variant="body1">{firm.address}</Typography>
        <Typography variant="body1">Contact: {firm.contact_number}</Typography>
      </Box>

      {/* Invoice & Bill To */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <Typography variant="h6">Invoice #: {sale.invoice_number}</Typography>
          <Typography>Date: {new Date(sale.date).toLocaleDateString()}</Typography>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: 'right' }}>
          <Typography variant="h6">Bill To:</Typography>
          <Typography>{sale.customer.name}</Typography>
          {/* Retail no GSTIN shown for customer */}
        </Grid>
      </Grid>

      {/* Items */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Unit ₹</TableCell>
              <TableCell>Tax ₹</TableCell>
              <TableCell>Line Total ₹</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sale.items.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>{item.product.name}</TableCell>
                <TableCell>{item.batch.batch_number}</TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>₹{item.final_price.toFixed(2)}</TableCell>
                <TableCell>₹{item.tax_amount.toFixed(2)}</TableCell>
                <TableCell>₹{(item.final_price * item.quantity + item.tax_amount).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals & Payments */}
      <Box sx={{ mb: 3, textAlign: 'right' }}>
        <Typography>Subtotal: ₹{(sale.total_amount - (sale.taxes_total || 0)).toFixed(2)}</Typography>
        <Typography>Tax Total: ₹{sale.taxes_total?.toFixed(2) || sale.items.reduce((s,i)=>(s+i.tax_amount),0).toFixed(2)}</Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="body1">Amount Paid: ₹{amountPaid.toFixed(2)}</Typography>
        <Typography variant="h6">Balance Due: ₹{balanceDue.toFixed(2)}</Typography>
        <Typography variant="h5" sx={{ mt: 1 }}>Grand Total: ₹{sale.total_amount.toFixed(2)}</Typography>
      </Box>

      {/* Footer & Signature */}
      <Grid container spacing={2} sx={{ mt: 4 }}>
        <Grid item xs={6}>
          <Typography variant="body2">{firm.invoice_footer}</Typography>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: 'right' }}>
          <Typography>Prepared By:</Typography>
          <Typography sx={{ mt: 2, mb: 4 }}>__________________________</Typography>
          <Typography>{sale.prepared_by_name || '________________'}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvoiceTemplate;